import { RotateCcw, VolumeX } from 'lucide-react';

import { Button } from '@/shared/ui/button';

import type { useFieldVoice } from '../api/use-field-voice';

interface Props {
  voice: ReturnType<typeof useFieldVoice>;
  colleagueName: string;
}

/** The microphone and colleague playback are independent parts of the same call. */
export function FieldVoiceSession({ voice, colleagueName }: Props) {
  if (voice.status === 'off') return null;
  const connecting = voice.status === 'connecting';
  const speaking = voice.status === 'speaking';
  const thinking = voice.status === 'thinking';
  const status = connecting
    ? 'Connecting microphone…'
    : speaking
      ? `${colleagueName} is speaking`
      : thinking
        ? `${colleagueName} is preparing a reply`
        : voice.muted
          ? 'Call paused'
          : 'Listening to you';
  const hint = connecting
    ? 'Your call starts when the microphone is ready.'
    : voice.muted
      ? 'Microphone muted. You can still hear your colleague.'
      : speaking
        ? 'You can interrupt by speaking.'
        : thinking
          ? 'Keep talking if you need to add something.'
          : 'Speak naturally. Pause when you’re done.';

  return (
    <section className="field-voice-session" aria-label={`Voice call with ${colleagueName}`}>
      <div className="field-voice-session-main">
        <div className="field-voice-state" role="status" aria-atomic="true">
          <p>{status}</p>
          <span>{hint}</span>
        </div>
        {speaking ? (
          <Button variant="ghost" size="sm" onClick={voice.stopAudio}>
            <VolumeX className="size-3.5" aria-hidden="true" />
            Stop speaking
          </Button>
        ) : voice.canReplay && !connecting && !thinking ? (
          <Button variant="ghost" size="sm" onClick={voice.replay}>
            <RotateCcw className="size-3.5" aria-hidden="true" />
            Replay reply
          </Button>
        ) : null}
      </div>
      {voice.partial ? (
        <p className="field-voice-caption" aria-label="Your live speech transcript">
          <span>You</span> {voice.partial}
        </p>
      ) : null}
    </section>
  );
}
